import { useEffect, useState } from 'react';
import pluralize from 'pluralize-ru';
import { useInjection } from 'inversify-react';
import useTranslate from '../hooks/useTranslate';
import pluralizeEn from '../helpers/pluralizeEn';
import { TICKETS_EVENTS } from '../constants/CONTROLLERS_EVENT_TYPES';
import type { ITicketsController } from '../lib/widget/tickets/tickets.interface';
import { TICKETS_MODULE_IDS } from '../lib/widget/tickets/tickets.types';
import { IStandingTicketController } from '../lib/widget_announcements/standingTickets/standingTicket.interface';
import { STANDING_TICKETS_MODULE_IDS } from '../lib/widget_announcements/standingTickets/standingTickets.const';
import type { IStandingTicketsEvents } from '../lib/widget_announcements/standingTickets/standingTickets.events';

export const useAnnouncement = (announcement) => {
  const ticketsController: ITicketsController = useInjection(TICKETS_MODULE_IDS.TICKETS_CONTROLLER);
  const standingTicketsController: IStandingTicketController = useInjection(
    STANDING_TICKETS_MODULE_IDS.STANDING_TICKETS_CONTROLLER,
  );
  const standingTicketsEvents: IStandingTicketsEvents = useInjection(
    STANDING_TICKETS_MODULE_IDS.STANDING_TICKETS_EVENTS,
  );
  const {
    Widget: {
      announcementsController: { selectedAnnouncements },
    },
  } = window;
  const [counter, setCounter] = useState(
    selectedAnnouncements[announcement.id] || announcement.selectedCount || null,
  );
  const { t, i18n } = useTranslate();

  const resetCounter = (ticket) => {
    if (announcement.id === ticket?.id) {
      setCounter(null);
    }
  };

  const updateCounter = (ticket) => {
    if (announcement.id === ticket.id) {
      setCounter(ticket.newCount);
    }
  };

  const showCounter = () => {
    setCounter(1);
    const { announcementsController } = window.Widget;

    announcementsController.addAnnouncement(announcement, 1);
  };

  const handleCounter = (count) => {
    setCounter(count === 0 ? null : count);
    const { announcementsController } = window.Widget;

    if (count === 0 || count < counter) {
      return announcementsController.deleteAnnouncement(announcement, count);
    }

    return announcementsController.addAnnouncement(announcement, count);
  };

  const getTicketsLabel = () => {
    switch (i18n.language) {
      case 'ru':
      case undefined:
        return pluralize(announcement.count, '', '%d билет', '%d билета', '%d билетов');
      default:
        return `${announcement.count} ${pluralizeEn(
          announcement.count,
          t('announcements_ticket_count'),
          t('announcements_tickets_count'),
        )}`;
    }
  };
  const clearQuantityAnnouncement = () => {
    setCounter(null);
  };

  useEffect(() => {
    ticketsController.addListener(ticketsController.events.REMOVE, resetCounter);
    window.Widget.announcementsController.addListener(
      TICKETS_EVENTS.CLEAR_QUANTITY_ANNOUNCEMENT,
      clearQuantityAnnouncement,
    );
    ticketsController.addListener(ticketsController.events.UPDATE_COUNT, updateCounter);
    standingTicketsController.addListener(
      standingTicketsEvents.SELECT_STANDING_TICKET,
      updateCounter,
    );

    return () => {
      ticketsController.removeListener(ticketsController.events.REMOVE, resetCounter);
      window.Widget.announcementsController.removeListener(
        TICKETS_EVENTS.CLEAR_QUANTITY_ANNOUNCEMENT,
        clearQuantityAnnouncement,
      );
      ticketsController.removeListener(ticketsController.events.UPDATE_COUNT, updateCounter);
      standingTicketsController.removeListener(
        standingTicketsEvents.SELECT_STANDING_TICKET,
        updateCounter,
      );
    };
  }, []);

  return {
    counter,
    setCounter,
    t,
    i18n,
    showCounter,
    handleCounter,
    ticketsCountLabel: getTicketsLabel(),
  };
};
